import React from 'react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, User, Mail, Phone, Calendar, Shield,
    MapPin, Building2, Badge, CheckCircle, HelpCircle, AlertTriangle,
    Plus, Trash2, Save
} from 'lucide-react';
import { fetchRoles } from '../apiHelper';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { useToast } from '../hooks/useToast';
import { client } from '../apiHelper';

const UserProfile = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { showToast } = useToast();

    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [rolesOptions, setRolesOptions] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [selectedRoleValue, setSelectedRoleValue] = useState('');

    // Extract the profile payload from react-router navigation state
    const profileData = location.state?.profileData;
    // console.log(profileData)

    // Locally track profile roles to safely add/remove before saving
    const [assignedRoles, setAssignedRoles] = useState(profileData?.roles || []);


    const {
        member_id,
        first_name,
        last_name,
        middle_name,
        profile_type,
        member_type,
        member_state,
        employeeid,
        email1,
        phone1,
        created_on,
        description,
        address,
        city,
        state,
        country,
        zip_code,
        branches = [],
        roles = []
    } = profileData;

    // Helper to format full name nicely
    const fullName = [first_name, middle_name, last_name].filter(Boolean).join(' ');

    // Match UI badges to backend flags
    const getProfileTypeBadge = (type) => {
        switch (type) {
            case 'E': return { label: 'Employee', classes: 'bg-teal-50 text-teal-700 border-teal-200' };
            case 'C': return { label: 'Customer', classes: 'bg-blue-50 text-blue-700 border-blue-200' };
            case 'B': return { label: 'Business User', classes: 'bg-purple-50 text-purple-700 border-purple-200' };
            default: return { label: 'General User', classes: 'bg-gray-50 text-gray-700 border-gray-200' };
        }
    };

    const getMemberStateBadge = (state) => {
        switch (state) {
            case 1: return { label: 'Verified', classes: 'bg-green-50 text-green-700 border-green-200' };
            case 2: return { label: 'Unverified', classes: 'bg-amber-50 text-amber-700 border-amber-200' };
            case 3: return { label: 'Not Applicable', classes: 'bg-slate-50 text-slate-600 border-slate-200' };
            case 4: return { label: 'Suspended', classes: 'bg-red-50 text-red-700 border-red-200' };
            default: return { label: 'Unknown', classes: 'bg-gray-50 text-gray-700 border-gray-200' };
        }
    };

    const profileBadge = getProfileTypeBadge(profile_type);
    const stateBadge = getMemberStateBadge(member_state);

    useEffect(() => {
        if (profileData?.roles) {
            setAssignedRoles(profileData.roles);
        }
    }, [profileData]);

    const handleOpenRoleModal = async () => {
        setIsRoleModalOpen(true);
        setLoadingRoles(true);
        try {
            const fetchedRoles = await fetchRoles()
            const mappedRoles = fetchedRoles.map(role => ({
                label: role.name,
                value: role.role_id,
            }));
            setRolesOptions(mappedRoles);
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to fetch roles', 'error');
        } finally {
            setLoadingRoles(false);
        }
    };

    const handleAddRoleToTable = (e) => {
        const roleId = e.target.value;
        if (!roleId) return;

        if (assignedRoles.some(r => r.role_id === Number(roleId))) {
            setSelectedRoleValue('');
            return;
        }

        const matchedOption = rolesOptions.find(opt => opt.value === Number(roleId));
        if (matchedOption) {
            setAssignedRoles(prev => [
                ...prev,
                { role_id: matchedOption.value, role_name: matchedOption.label }
            ]);
        }
        setSelectedRoleValue('');
    };

    const handleRemoveRoleFromTable = (roleId) => {
        setAssignedRoles(prev => prev.filter(r => r.role_id !== roleId));
    };

    const handleSaveRolesPayload = async () => {
        const roleIdsPayload = assignedRoles.map(r => r.role_id);
        try {
            const response = await client.postData('/users/assign-roles', { 'member_id': profileData.member_id, 'role_ids': roleIdsPayload });
            showToast(response.msg || "Successfully assigned roles", "success");
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to assign roles to user.", "error");
        } finally {
            setIsRoleModalOpen(false);
        }
    }

    // Handle missing state gracefully (e.g., direct page refresh)
    if (!profileData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
                <AlertTriangle size={48} className="text-amber-500 mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">No Profile Data Found</h3>
                <p className="text-gray-600 mb-6">We couldn't find the details for this user profile.</p>
                <Button variant="primary" onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} className="mr-2" /> Go Back
                </Button>
            </div>
        );
    }

    const handleExcludeFromPayroll = async () => {
        try {
            await client.fetchData(`/payroll/exclude/${member_id}`);
            showToast("User excluded from payroll successfully", "success");
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to exclude user from payroll", "error");
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            {/* Back Button */}
            <div className="mb-6">
                <Button variant="secondary" onClick={() => navigate(-1)} className="flex items-center gap-2">
                    <ArrowLeft size={16} /> Go Back
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Avatar & Quick Summary */}
                <Card className="p-6 flex flex-col items-center text-center h-fit shadow-md">
                    <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mb-4 ring-4 ring-blue-50">
                        <User size={44} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 break-words max-w-full">
                        {fullName || description || 'Unnamed User'}
                    </h2>
                    <p className="text-sm text-gray-500 font-mono mt-1">{employeeid || 'No ID Assigned'}</p>

                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${profileBadge.classes}`}>
                            {profileBadge.label}
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${stateBadge.classes}`}>
                            {stateBadge.label}
                        </span>
                    </div>

                    {(profileData.profile_type.trim() === 'E' || profileData.profile_type.trim() === 'B') && (
                        <Button
                            variant="danger"
                            size="sm"
                            className="mt-4 w-full"
                            onClick={handleExcludeFromPayroll}
                        >
                            Exclude from Payroll
                        </Button>
                    )}

                    <hr className="w-full my-6 border-gray-200" />

                    {/* Basic Meta Details */}
                    <div className="w-full space-y-3 text-left text-sm text-gray-600">
                        <div className="flex items-center gap-3">
                            <Badge size={16} className="text-gray-400 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-400">Member Type Flag</p>
                                <p className="font-medium text-gray-800">{member_type === 'O' ? 'Organization' : 'Individual User'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Calendar size={16} className="text-gray-400 shrink-0" />
                            <div>
                                <p className="text-xs text-gray-400">Created On</p>
                                <p className="font-medium text-gray-800">
                                    {created_on ? new Date(created_on).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Right Column: Detailed breakdowns split into cards */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Card 1: Contact Information */}
                    <Card className="p-6 shadow-md">
                        <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Contact Details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex items-start gap-3">
                                <Mail size={18} className="text-gray-400 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Email Address</p>
                                    <p className="text-sm font-semibold text-gray-800 break-all">{email1 || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Phone size={18} className="text-gray-400 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Phone Number</p>
                                    <p className="text-sm font-semibold text-gray-800">{phone1 || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 sm:col-span-2">
                                <MapPin size={18} className="text-gray-400 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Physical Address</p>
                                    {address || city || state ? (
                                        <p className="text-sm font-semibold text-gray-800">
                                            {[address, city, state, zip_code, country].filter(Boolean).join(', ')}
                                        </p>
                                    ) : (
                                        <p className="text-sm font-semibold text-gray-400 italic">No address on file</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Card 2: System Roles */}
                    <Card className="p-6 shadow-md">
                        <div className="flex items-center justify-between mb-4 border-b pb-2">
                            <div className="flex items-center gap-2">
                                <Shield size={20} className="text-indigo-500" />
                                <h3 className="text-lg font-bold text-gray-800">Assigned Roles</h3>
                            </div>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="flex items-center gap-1.5 py-1 px-2.5 text-xs font-semibold"
                                onClick={handleOpenRoleModal}
                            >
                                <Plus size={14} /> Manage Roles
                            </Button>
                        </div>
                        {assignedRoles.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {assignedRoles.map((role) => (
                                    <span
                                        key={role.role_id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-100 text-sm font-medium rounded-md shadow-sm"
                                    >
                                        <CheckCircle size={14} />
                                        {role.role_name}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-gray-500 bg-gray-50 border border-dashed rounded-lg p-4">
                                <HelpCircle size={18} />
                                <p className="text-sm">No specific system roles configured for this account tier.</p>
                            </div>
                        )}
                    </Card>

                    {/* Card 3: Branch Assignments */}
                    <Card className="p-6 shadow-md">
                        <div className="flex items-center gap-2 mb-4 border-b pb-2">
                            <Building2 size={20} className="text-emerald-500" />
                            <h3 className="text-lg font-bold text-gray-800">Branch Access Control</h3>
                        </div>
                        {branches.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {branches.map((branch) => (
                                    <div
                                        key={branch.store_id}
                                        className="flex items-center p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-emerald-200 transition-colors"
                                    >
                                        <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-md flex items-center justify-center mr-3 font-semibold text-xs">
                                            #{branch.store_id}
                                        </div>
                                        <span className="text-sm font-medium text-gray-700 truncate">
                                            {branch.identifier}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-gray-500 bg-gray-50 border border-dashed rounded-lg p-4">
                                <HelpCircle size={18} />
                                <p className="text-sm">This account is not mapped to any operational branches.</p>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
            <Modal
                isOpen={isRoleModalOpen}
                onClose={() => setIsRoleModalOpen(false)}
                title="Manage Account Roles"
                size="md"
            >
                <div className="space-y-5">
                    {/* Role Input Picker */}
                    <SearchableSelectBox
                        name="modal_role_picker"
                        label="Search & Add Role"
                        placeholder="Type or select a system role..."
                        options={rolesOptions}
                        value={selectedRoleValue}
                        onChange={handleAddRoleToTable}
                        loading={loadingRoles}
                    />

                    {/* Roles Table List */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Assigned Roles Preview
                        </label>
                        <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg shadow-inner">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Role ID</th>
                                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Role Name</th>
                                        <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {assignedRoles.length > 0 ? (
                                        assignedRoles.map((role) => (
                                            <tr key={role.role_id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-2 font-mono text-gray-600">#{role.role_id}</td>
                                                <td className="px-4 py-2 font-medium text-gray-800">{role.role_name}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveRoleFromTable(role.role_id)}
                                                        className="p-1 text-red-500 hover:text-red-700 rounded hover:bg-red-50 transition-colors"
                                                        title="Remove Role"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="px-4 py-6 text-center text-gray-400 italic">
                                                No roles listed. Select a role above to add.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Modal Bottom Actions */}
                    <div className="flex justify-end gap-3 pt-3 border-t">
                        <Button variant="secondary" onClick={() => setIsRoleModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="flex items-center gap-2" onClick={handleSaveRolesPayload}>
                            <Save size={16} /> Save Roles
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default UserProfile;